import { useState } from 'react';
import { CopyPlus, Pencil, Plus } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { ServicePackageRevision } from '../types';
import {
  RevisionFormState,
  emptyRevisionForm,
  revisionToForm,
  formatCents,
  latestRevisionForTerm,
  uniqueTerms,
  SEGMENT_LABELS,
} from '../types';
import { ServicePackageRevisionDialog } from './ServicePackageRevisionDialog';
import i18n from '@/utils/i18n';

interface ServicePackageRevisionTableProps {
  revisions: ServicePackageRevision[];
  currency?: string;
  /** Called when an existing term is edited and saved */
  onSave: (term: number, data: RevisionFormState) => Promise<void>;
  /** Called when a term is copied to a new term via the backend mutation */
  onCopyToTerm: (sourceRevisionId: string, targetTerm: number) => Promise<void>;
}

export function ServicePackageRevisionTable({
  revisions,
  currency = 'EUR',
  onSave,
  onCopyToTerm,
}: ServicePackageRevisionTableProps) {
  // ── Edit existing term ─────────────────────────────────────────────────────
  const [editingTerm, setEditingTerm] = useState<number | null>(null);
  const [form, setForm] = useState<RevisionFormState>(emptyRevisionForm());
  const [saving, setSaving] = useState(false);

  // ── Copy-as-new-term picker ───────────────────────────────────────────────
  const [showCopyPicker, setShowCopyPicker] = useState(false);
  const [copySourceRevId, setCopySourceRevId] = useState('');
  const [copyPickerMonths, setCopyPickerMonths] = useState('');
  const [copying, setCopying] = useState(false);

  // ── Add-blank-term picker ─────────────────────────────────────────────────
  const [showBlankPicker, setShowBlankPicker] = useState(false);
  const [blankPickerMonths, setBlankPickerMonths] = useState('');

  const terms = uniqueTerms(revisions);

  const openEdit = (term: number) => {
    const current = latestRevisionForTerm(revisions, term);
    setForm(current ? revisionToForm(current) : emptyRevisionForm());
    setEditingTerm(term);
  };

  /** Opens the copy-term picker pre-seeded with the source revision id */
  const openCopyAsNewTerm = (sourceRev: ServicePackageRevision) => {
    setCopySourceRevId(sourceRev.id);
    setCopyPickerMonths('');
    setShowCopyPicker(true);
  };

  const handleSave = async () => {
    if (editingTerm === null) return;
    setSaving(true);
    try {
      await onSave(editingTerm, form);
      setEditingTerm(null);
    } finally {
      setSaving(false);
    }
  };

  const closeCopyPicker = () => {
    setShowCopyPicker(false);
    setCopySourceRevId('');
    setCopyPickerMonths('');
  };

  const copyPickerMonthsParsed = parseInt(copyPickerMonths, 10);
  const copyPickerMonthsValid =
    !isNaN(copyPickerMonthsParsed) &&
    copyPickerMonthsParsed > 0 &&
    !terms.includes(copyPickerMonthsParsed);

  const handleConfirmCopyTerm = async () => {
    if (!copyPickerMonthsValid || !copySourceRevId) return;
    setCopying(true);
    try {
      await onCopyToTerm(copySourceRevId, copyPickerMonthsParsed);
      closeCopyPicker();
    } finally {
      setCopying(false);
    }
  };

  const closeBlankPicker = () => {
    setShowBlankPicker(false);
    setBlankPickerMonths('');
  };

  const blankPickerMonthsParsed = parseInt(blankPickerMonths, 10);
  const blankPickerMonthsValid =
    !isNaN(blankPickerMonthsParsed) &&
    blankPickerMonthsParsed > 0 &&
    !terms.includes(blankPickerMonthsParsed);

  const handleConfirmBlankTerm = () => {
    if (!blankPickerMonthsValid) return;
    setForm(emptyRevisionForm());
    setEditingTerm(blankPickerMonthsParsed);
    closeBlankPicker();
  };

  return (
    <>
      <Card className="tw:p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="tw:pl-4 tw:h-13 tw:w-[110px]">
                {i18n.t('ServicePackage:term')}
              </TableHead>
              <TableHead>{i18n.t('ServicePackage:monthlyFee')}</TableHead>
              <TableHead>{i18n.t('ServicePackage:onboarding')}</TableHead>
              <TableHead>{i18n.t('ServicePackage:opsOrder')}</TableHead>
              <TableHead className="tw:w-[90px]">
                {i18n.t('ServicePackage:freeCheckouts')}
              </TableHead>
              <TableHead>{i18n.t('ServicePackage:includedItems')}</TableHead>
              {/* wider to accommodate two icon buttons */}
              <TableHead className="tw:w-[80px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {terms.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="tw:pl-4 tw:py-8 tw:text-sm tw:text-muted-foreground"
                >
                  {i18n.t('ServicePackage:noTermsConfiguredYet')}
                </TableCell>
              </TableRow>
            ) : (
              terms.map((term) => {
                const current = latestRevisionForTerm(revisions, term)!;
                return (
                  <TableRow
                    key={term}
                    className="tw:*:first:pl-4 tw:*:last:pr-3"
                  >
                    <TableCell>
                      <span className="tw:text-sm tw:font-medium">
                        {i18n.t('ServicePackage:nMonths', { n: term })}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="tw:text-sm">
                        {formatCents(
                          current.monthly_service_fee_in_cents,
                          currency,
                        )}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="tw:text-sm">
                        {formatCents(current.onboarding_fee_in_cents, currency)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="tw:text-sm">
                        {formatCents(
                          current.ops_order_price_in_cents,
                          currency,
                        )}
                      </span>
                    </TableCell>
                    <TableCell>
                      {current.free_checkouts.length > 0 ? (
                        <div className="tw:flex tw:flex-wrap tw:gap-1">
                          {current.free_checkouts.map((fc) => (
                            <Badge
                              key={fc.segment}
                              variant="secondary"
                              className="tw:text-xs tw:font-normal"
                            >
                              {SEGMENT_LABELS[fc.segment]}: {fc.amount}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <span className="tw:text-xs tw:text-muted-foreground">
                          —
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      {current.allowances.length > 0 ? (
                        <div className="tw:flex tw:flex-wrap tw:gap-1">
                          {current.allowances.map((a) => (
                            <Badge
                              key={a.item.id}
                              variant="secondary"
                              className="tw:text-xs tw:font-normal"
                            >
                              {a.item.name}: {a.monthly_quantity}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <span className="tw:text-xs tw:text-muted-foreground">
                          —
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="tw:flex tw:gap-1">
                        <Button
                          variant="outline"
                          size="icon-sm"
                          onClick={() => openEdit(term)}
                          title={i18n.t('Common:edit')}
                        >
                          <Pencil className="tw:size-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="icon-sm"
                          onClick={() => openCopyAsNewTerm(current)}
                          title={i18n.t('ServicePackage:copyAsNewTerm')}
                        >
                          <CopyPlus className="tw:size-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Add term button */}
      <div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowBlankPicker(true)}
        >
          <Plus className="tw:size-4" />
          {i18n.t('ServicePackage:addTerm')}
        </Button>
      </div>

      {/* Copy-as-new-term picker dialog */}
      <Dialog
        open={showCopyPicker}
        onOpenChange={(o) => !o && !copying && closeCopyPicker()}
      >
        <DialogContent className="tw:max-w-sm">
          <DialogHeader>
            <DialogTitle>{i18n.t('ServicePackage:copyTermTitle')}</DialogTitle>
          </DialogHeader>

          <div className="tw:flex tw:flex-col tw:gap-4 tw:py-2">
            <p className="tw:text-sm tw:text-muted-foreground">
              {i18n.t('ServicePackage:copyTermDescription')}
            </p>
            <div className="tw:flex tw:flex-col tw:gap-1.5">
              <Label htmlFor="copy-picker-months">
                {i18n.t('ServicePackage:newTermMonths')}
              </Label>
              <Input
                id="copy-picker-months"
                type="number"
                min={1}
                placeholder="e.g. 18"
                value={copyPickerMonths}
                onChange={(e) => setCopyPickerMonths(e.target.value)}
                disabled={copying}
              />
              {copyPickerMonths !== '' &&
                !isNaN(copyPickerMonthsParsed) &&
                terms.includes(copyPickerMonthsParsed) && (
                  <span className="tw:text-xs tw:text-destructive">
                    {i18n.t('ServicePackage:termAlreadyExists', {
                      n: copyPickerMonthsParsed,
                    })}
                  </span>
                )}
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={closeCopyPicker}
              disabled={copying}
            >
              {i18n.t('Common:cancel')}
            </Button>
            <Button
              onClick={handleConfirmCopyTerm}
              disabled={!copyPickerMonthsValid || copying}
            >
              {copying
                ? i18n.t('ServicePackage:copying')
                : i18n.t('ServicePackage:copy')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add blank term picker */}
      <Dialog
        open={showBlankPicker}
        onOpenChange={(o) => !o && closeBlankPicker()}
      >
        <DialogContent className="tw:max-w-sm">
          <DialogHeader>
            <DialogTitle>{i18n.t('ServicePackage:addNewTerm')}</DialogTitle>
          </DialogHeader>

          <div className="tw:flex tw:flex-col tw:gap-4 tw:py-2">
            <div className="tw:flex tw:flex-col tw:gap-1.5">
              <Label htmlFor="blank-picker-months">
                {i18n.t('ServicePackage:contractTermMonths')}
              </Label>
              <Input
                id="blank-picker-months"
                type="number"
                min={1}
                placeholder="e.g. 18"
                value={blankPickerMonths}
                onChange={(e) => setBlankPickerMonths(e.target.value)}
              />
              {blankPickerMonths !== '' &&
                !isNaN(blankPickerMonthsParsed) &&
                terms.includes(blankPickerMonthsParsed) && (
                  <span className="tw:text-xs tw:text-destructive">
                    {i18n.t('ServicePackage:termAlreadyExists', {
                      n: blankPickerMonthsParsed,
                    })}
                  </span>
                )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeBlankPicker}>
              {i18n.t('Common:cancel')}
            </Button>
            <Button
              onClick={handleConfirmBlankTerm}
              disabled={!blankPickerMonthsValid}
            >
              {i18n.t('ServicePackage:configure')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit / configure dialog */}
      {editingTerm !== null && (
        <ServicePackageRevisionDialog
          open
          onOpenChange={(o) => !o && !saving && setEditingTerm(null)}
          term={editingTerm}
          isFirstRevision={!latestRevisionForTerm(revisions, editingTerm)}
          form={form}
          onChange={setForm}
          onSave={handleSave}
          saving={saving}
          currency={currency}
        />
      )}
    </>
  );
}
